// What a TV remembers about itself (T.4, T.6, 24 Oct 2026). A TV is switched off at the wall every night and comes back with a browser that opens its home page:
// it has to find its way back to the party without anybody reaching for a phone. The link it was given is kept in the same place the installed wall keeps
// its link (wall/remember.ts); the only extra thing kept is a mark that says "this device was paired as a TV", so a link that stops working sends it back to the
// pairing screen and not to a notice nobody can act on from a sofa. Nothing leaves the device.
import { codeFromPath } from '../lib/code'
import { forgetWall, recallWall, rememberWall } from './remember'

export const TV_MARK = 'kwento.tv'
/** When the paired event's window closes (ISO), as the server last said. Lets a TV that wakes up with no network know the party is over. */
export const TV_END = 'kwento.tv.ends'
/** The name of the thumbnail cache of the service worker (public/sw.js); a TV that lets go of an event empties it. */
export const MEDIA_CACHE = 'kwento-media'
/** The device clock of a TV is often wrong; the end is trusted at start-up only an hour after it, and the server's own answer decides the rest. */
export const END_GRACE_MS = 60 * 60_000

type Store = Pick<Storage, 'getItem' | 'setItem' | 'removeItem'>

export function markTv(store: Store | null): void {
  try {
    store?.setItem(TV_MARK, '1')
  } catch {
    // Private mode: the TV works the same, it just does not find its way back by itself.
  }
}

export function isTvDevice(store: Store | null): boolean {
  try {
    return store?.getItem(TV_MARK) === '1'
  } catch {
    return false
  }
}

/** The token out of the wall path the server hands a paired TV (/wall/{token}), or null. */
export function tokenOfWallPath(path: string): string | null {
  return codeFromPath(path, 'wall')
}

/**
 * Where a TV goes once the server has handed it its wall: the wall without a link in the address, so that the link is the remembered one and a link that
 * stops working is forgotten at once. Where the device cannot remember (storage blocked), the wall at its own address, as before.
 */
export function openPairedWall(store: Store | null, wallPath: string): string {
  // Only a path on this site (Semgrep js-open-redirect, 29 Oct 2026): the answer comes from our own API, but a TV never leaves for another host on its say.
  if (!wallPath.startsWith('/') || wallPath.startsWith('//')) return '/tv'
  const token = tokenOfWallPath(wallPath)
  if (!store || !token) return wallPath
  rememberWall(store, token)
  if (recallWall(store) !== token) return wallPath
  markTv(store)
  return '/wall/'
}

/**
 * What /tv does when it opens: straight back to the wall it showed last, or the pairing screen. "/tv?new" is the way to pair a TV with another event from
 * the TV itself: it forgets the old link first.
 */
export function startOf(store: Store | null, search: string, nowMs: number = Date.now()): 'wall' | 'pair' {
  if (new URLSearchParams(search).has('new')) {
    letGo(store)
    return 'pair'
  }
  if (recallWall(store) && endedAtStart(store, nowMs)) {
    // Never the wall of a party that is over, not even for a moment and not without a network (Tom, 24 Oct 2026).
    letGo(store)
    return 'pair'
  }
  return recallWall(store) ? 'wall' : 'pair'
}

/** Keeps when the paired event ends, for the next start-up. Only a TV keeps it, and only a real date. */
export function rememberEnd(store: Store | null, iso: string | null | undefined): void {
  if (!store || !iso || !isTvDevice(store) || Number.isNaN(Date.parse(iso))) return
  try {
    if (store.getItem(TV_END) !== iso) store.setItem(TV_END, iso)
  } catch {
    // nothing to do
  }
}

/** The kept end has passed (with the grace for a clock that is wrong): the event this TV was paired with is over. */
export function endedAtStart(store: Store | null, nowMs: number): boolean {
  try {
    const iso = store?.getItem(TV_END)
    const ends = iso ? Date.parse(iso) : NaN
    return !Number.isNaN(ends) && nowMs > ends + END_GRACE_MS
  } catch {
    return false
  }
}

/** Set once this page has let go of its event: nothing it still hears from the server may take the link up again before the pairing screen is opened. */
let released = false
export const isReleased = (): boolean => released

/** The TV lets go of its event: the link and the end are forgotten. It stays a TV (the mark stays), so it goes back to the pairing screen. */
export function letGo(store: Store | null): void {
  released = true
  forgetWall(store)
  try {
    store?.removeItem(TV_END)
  } catch {
    // nothing to do
  }
}

/** The pictures of the event it had are removed from the device: the thumbnail cache of the service worker. Never throws; a TV with no cache has nothing to remove. */
export async function clearPictures(cacheStorage: Pick<CacheStorage, 'delete'> | undefined = typeof caches === 'undefined' ? undefined : caches): Promise<void> {
  try {
    await cacheStorage?.delete(MEDIA_CACHE)
  } catch {
    // nothing to do
  }
}

/** A wall that says the event has ended: a paired TV goes back to pairing (after the notice has been on long enough to read). */
export function afterEndedWall(store: Store | null): '/tv' | null {
  return isTvDevice(store) ? '/tv' : null
}

/** After how long a wall that the server no longer knows sends a TV back to pairing, long enough to read the notice. */
export const BACK_TO_PAIRING_MS = 8_000

/** A wall that was opened from storage and is found invalid: a paired TV goes back to pairing, anything else stays on the notice. */
export function afterInvalidWall(store: Store | null, openedFromStorage: boolean): '/tv' | null {
  return openedFromStorage && isTvDevice(store) ? '/tv' : null
}

/**
 * Whether the browser says it is on a television. Only when it is sure: a phone or computer sent to the TV page by mistake would be a worse mistake than a
 * TV left on the home page, which has the address typed in anyway.
 */
export function isTvBrowser(ua: string): boolean {
  return TV_PATTERNS.some((p) => p.test(ua))
}

const TV_PATTERNS: RegExp[] = [
  /\bSMART-?TV\b/i, // Samsung Tizen, many others
  /\bWeb0S\b|\bwebOS\.TV\b|\bNetCast\b/i, // LG
  /\bHbbTV\b/i, // European and Asian broadcast browsers
  /\bCrKey\b|\bGoogleTV\b/i, // Chromecast, older Google TV
  /\bAFT[A-Z0-9]{1,6}\b/, // Amazon Fire TV (AFTMM, AFTSS, ...)
  /\bBRAVIA\b/i, // Sony
  /\bVIDAA\b|\bHisense\b.*\bTV\b/i,
  /\bRoku\b/i,
  /\bAndroid[^)]*\bTV\b/i, // "Android 9; ... TV Build"
  /\bTizen\b[^)]*\bTV\b|\bTV Safari\b/i,
  /\bOpera TV\b|\bLarge Screen\b/i,
]
